import { useEffect, useState, useRef, useCallback } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";

// ── Available template variables for OT ──
const OT_VARIABLES = [
  { key: "patient_name",      label: "Patient Name",       sample: "Mr. Rahul Sharma" },
  { key: "patient_uhid",      label: "UHID",               sample: "SH00100123" },
  { key: "patient_age",       label: "Age",                sample: "25" },
  { key: "patient_gender",    label: "Gender",             sample: "Male" },
  { key: "patient_mobile",    label: "Mobile",             sample: "9876543210" },
  { key: "patient_address",   label: "Address",            sample: "123 Main St, Delhi" },
  { key: "patient_ipd_no",    label: "IPD No",             sample: "000456" },
  { key: "ot_number",         label: "OT Number",          sample: "OT12345" },
  { key: "ot_date",           label: "OT Date",            sample: "24-Jun-2026 10:30 AM" },
  { key: "ot_reg_date",       label: "Reg Date / DOA",     sample: "24-Jun-2026" },
  { key: "ot_case",           label: "Case Name",          sample: "Appendectomy" },
  { key: "ot_doctor",         label: "OT Doctor",          sample: "Dr. Amit Singh" },
  { key: "ot_asst_doctor",    label: "Asst Doctor",        sample: "Dr. Priya Gupta" },
  { key: "ot_anesthesia_doc", label: "Anesthesia Doctor",  sample: "Dr. Ravi Kumar" },
  { key: "ot_ref_doctor",     label: "Refer Doctor",       sample: "Dr. Suresh Patel" },
  { key: "ot_nurse",          label: "Nurse Name",         sample: "Nurse Rani" },
  { key: "ot_pediatric_doc",  label: "Pediatric Doctor",   sample: "Dr. Meena" },
  { key: "ot_bed",            label: "Bed",                sample: "Bed-12" },
  { key: "ot_ward",           label: "Ward",               sample: "General Ward" },
  { key: "ot_purpose",        label: "Purpose",            sample: "Emergency Surgery" },
  { key: "consultant_name",   label: "Consultant Name",    sample: "Dr. Amit Singh" },
  { key: "current_date",      label: "Current Date",       sample: "24-Jun-2026" },
  { key: "current_time",      label: "Current Time",       sample: "10:30 AM" },
];

function OtFormat() {
  // ── Button list ──
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Button form ──
  const [editBtnId, setEditBtnId] = useState(null);
  const [btnName, setBtnName] = useState("");
  const [btnColor, setBtnColor] = useState("#405189");
  const [btnOrder, setBtnOrder] = useState(0);
  const [btnPageSize, setBtnPageSize] = useState("a4");
  const [btnOrientation, setBtnOrientation] = useState("portrait");
  const [savingBtn, setSavingBtn] = useState(false);

  // ── Page editor ──
  const [activeBtn, setActiveBtn] = useState(null); // button being edited for pages
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);

  // ── Single page editor ──
  const [editPage, setEditPage] = useState(null); // page being edited
  const [pageImageFile, setPageImageFile] = useState(null);
  const [pageImagePreview, setPageImagePreview] = useState(null);
  const [pageVariables, setPageVariables] = useState([]);
  const [pageOrder, setPageOrder] = useState(1);
  const [savingPage, setSavingPage] = useState(false);

  // ── Drag state ──
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  // ══════════════════════════════════════════════════════
  //  INIT
  // ══════════════════════════════════════════════════════
  useEffect(() => { loadButtons(); }, []);

  const loadButtons = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      const res = await axios.post(AppConst.BASE_URL + AppConst.getOtFormatBtnsUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) setButtons(res.data.records || []);
      else setButtons([]);
    } catch { setButtons([]); }
    finally { setLoading(false); }
  };

  const loadPages = async (btnId) => {
    setPagesLoading(true);
    try {
      const fd = new FormData();
      fd.append("btn_id", btnId);
      const res = await axios.post(AppConst.BASE_URL + AppConst.getOtFormatPagesUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) setPages(res.data.pages || []);
      else setPages([]);
    } catch { setPages([]); }
    finally { setPagesLoading(false); }
  };

  // ── Button CRUD ──
  const handleSaveBtn = async () => {
    if (!btnName.trim()) { toast.error("Button name required"); return; }
    setSavingBtn(true);
    try {
      const fd = new FormData();
      if (editBtnId) fd.append("btn_id", editBtnId);
      fd.append("btn_name", btnName);
      fd.append("btn_color", btnColor);
      fd.append("btn_order", btnOrder);
      fd.append("btn_page_size", btnPageSize);
      fd.append("btn_orientation", btnOrientation);
      const res = await axios.post(AppConst.BASE_URL + AppConst.saveOtFormatBtnUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        toast.success(res.data.success);
        resetBtnForm();
        loadButtons();
      } else toast.error(res.data.success);
    } catch { toast.error("Server error"); }
    finally { setSavingBtn(false); }
  };

  const handleEditBtn = (btn) => {
    setEditBtnId(btn.btn_id);
    setBtnName(btn.btn_name);
    setBtnColor(btn.btn_color || "#405189");
    setBtnOrder(btn.btn_order || 0);
    setBtnPageSize(btn.btn_page_size || "a4");
    setBtnOrientation(btn.btn_orientation || "portrait");
  };

  const handleDeleteBtn = async (id) => {
    if (!window.confirm("Delete this button and all its pages?")) return;
    try {
      const fd = new FormData();
      fd.append("btn_id", id);
      const res = await axios.post(AppConst.BASE_URL + AppConst.deleteOtFormatBtnUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) { toast.success("Deleted"); loadButtons(); if (activeBtn?.btn_id === id) { setActiveBtn(null); setPages([]); } }
    } catch { toast.error("Delete failed"); }
  };

  const resetBtnForm = () => {
    setEditBtnId(null); setBtnName(""); setBtnColor("#405189"); setBtnOrder(0);
    setBtnPageSize("a4"); setBtnOrientation("portrait");
  };

  // ── Page CRUD ──
  const openPageEditor = (btn) => {
    setActiveBtn(btn);
    loadPages(btn.btn_id);
    resetPageForm();
  };

  const handlePageImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPageImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPageImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSavePage = async () => {
    if (!pageImageFile && !editPage?.page_image) { toast.error("Upload an image"); return; }
    setSavingPage(true);
    try {
      const fd = new FormData();
      if (editPage?.page_id) fd.append("page_id", editPage.page_id);
      fd.append("page_btn_id", activeBtn.btn_id);
      fd.append("page_order", pageOrder);
      fd.append("page_variables", JSON.stringify(pageVariables));
      if (pageImageFile) fd.append("page_image_file", pageImageFile);
      const res = await axios.post(AppConst.BASE_URL + AppConst.saveOtFormatPageUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        toast.success(res.data.success);
        resetPageForm();
        loadPages(activeBtn.btn_id);
        loadButtons();
      } else toast.error(res.data.success);
    } catch { toast.error("Server error"); }
    finally { setSavingPage(false); }
  };

  const handleEditPage = (pg) => {
    setEditPage(pg);
    setPageOrder(pg.page_order || 1);
    setPageImageFile(null);
    setPageImagePreview(pg.page_image ? AppConst.BASE_URL + "/" + pg.page_image : null);
    try { setPageVariables(JSON.parse(pg.page_variables || "[]")); } catch { setPageVariables([]); }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm("Delete this page?")) return;
    try {
      const fd = new FormData();
      fd.append("page_id", pageId);
      await axios.post(AppConst.BASE_URL + AppConst.deleteOtFormatPageUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Page deleted");
      loadPages(activeBtn.btn_id);
      loadButtons();
    } catch { toast.error("Failed"); }
  };

  const resetPageForm = () => {
    setEditPage(null); setPageImageFile(null); setPageImagePreview(null);
    setPageVariables([]); setPageOrder((pages.length || 0) + 1);
  };

  // ── Variable helpers ──
  const addVariable = (v) => {
    if (pageVariables.find(pv => pv.key === v.key)) { toast.warning("Already added"); return; }
    setPageVariables(prev => [...prev, { ...v, x: 10, y: 10 + prev.length * 4, fontSize: 12, fontColor: "#000000", fontWeight: "normal" }]);
  };
  const removeVariable = (idx) => setPageVariables(prev => prev.filter((_, i) => i !== idx));
  const updateVariable = (idx, field, value) => setPageVariables(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));

  // ── Drag ──
  const getCanvasRect = () => canvasRef.current?.getBoundingClientRect() || null;

  const handleMouseDown = (idx, e) => {
    e.preventDefault();
    const rect = getCanvasRect();
    if (!rect) return;
    setDraggingIdx(idx);
    setDragOffset({
      x: e.clientX - rect.left - (pageVariables[idx].x / 100) * rect.width,
      y: e.clientY - rect.top - (pageVariables[idx].y / 100) * rect.height,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (draggingIdx === null) return;
    const rect = getCanvasRect();
    if (!rect) return;
    const pctX = Math.max(0, Math.min(95, ((e.clientX - rect.left - dragOffset.x) / rect.width) * 100));
    const pctY = Math.max(0, Math.min(95, ((e.clientY - rect.top - dragOffset.y) / rect.height) * 100));
    setPageVariables(prev => prev.map((v, i) => i === draggingIdx ? { ...v, x: +pctX.toFixed(2), y: +pctY.toFixed(2) } : v));
  }, [draggingIdx, dragOffset]);

  const handleMouseUp = useCallback(() => setDraggingIdx(null), []);

  useEffect(() => {
    if (draggingIdx !== null) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => { window.removeEventListener("mousemove", handleMouseMove); window.removeEventListener("mouseup", handleMouseUp); };
  }, [draggingIdx, handleMouseMove, handleMouseUp]);

  const currentImgSrc = pageImagePreview || (editPage?.page_image ? AppConst.BASE_URL + "/" + editPage.page_image : null);

  // ══════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════
  return (
    <div className="page-content">
      <div className="container-fluid">
        <h2 style={{ color: "#405189" }}>OT Format Manager</h2>
        <p className="text-muted">Create print buttons (Anesthesia, Consent, custom), add multi-page templates with variable positions.</p>

        <div className="row">
          {/* ═══ LEFT: Button Management ═══ */}
          <div className="col-md-5">
            {/* Add/Edit Button Form */}
            <div className="card">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{editBtnId ? "Edit Button" : "Add New Button"}</h6>
                {editBtnId && <button className="btn btn-light btn-sm" onClick={resetBtnForm}>Cancel</button>}
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 12 }}>Button Name *</label>
                    <input className="form-control form-control-sm" value={btnName} onChange={e => setBtnName(e.target.value)} placeholder="e.g. Anesthesia Form" />
                  </div>
                  <div className="col-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Color</label>
                    <input type="color" className="form-control form-control-sm p-0" value={btnColor} onChange={e => setBtnColor(e.target.value)} style={{ height: 32 }} />
                  </div>
                  <div className="col-3">
                    <label className="form-label" style={{ fontSize: 12 }}>Order</label>
                    <input type="number" className="form-control form-control-sm" value={btnOrder} onChange={e => setBtnOrder(e.target.value)} min={0} />
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 12 }}>Page Size</label>
                    <select className="form-control form-control-sm" value={btnPageSize} onChange={e => setBtnPageSize(e.target.value)}>
                      <option value="a4">A4</option><option value="a5">A5</option><option value="letter">Letter</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 12 }}>Orientation</label>
                    <select className="form-control form-control-sm" value={btnOrientation} onChange={e => setBtnOrientation(e.target.value)}>
                      <option value="portrait">Portrait</option><option value="landscape">Landscape</option>
                    </select>
                  </div>
                </div>
                {/* Preview */}
                <div className="mt-3">
                  <label className="form-label" style={{ fontSize: 11 }}>Preview:</label>
                  <button type="button" className="btn btn-sm ms-2" style={{ background: btnColor, color: "#fff", border: "none" }} disabled>
                    {btnName || "Button Name"}
                  </button>
                </div>
                <div className="text-end mt-2">
                  <button className="btn btn-primary btn-sm" onClick={handleSaveBtn} disabled={savingBtn}>
                    {savingBtn ? "Saving..." : (editBtnId ? "Update" : "Add Button")}
                  </button>
                </div>
              </div>
            </div>

            {/* Button List */}
            <div className="card">
              <div className="card-header"><h6 className="mb-0" style={{ color: "#405189" }}>Saved Buttons</h6></div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-3"><span className="spinner-border text-primary" /></div>
                ) : buttons.length === 0 ? (
                  <div className="text-center py-3 text-muted">No buttons yet.</div>
                ) : (
                  <div className="list-group list-group-flush">
                    {buttons.map((btn, idx) => (
                      <div key={btn.btn_id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <button type="button" className="btn btn-sm me-2" style={{ background: btn.btn_color || "#405189", color: "#fff", border: "none", fontSize: 11, padding: "3px 10px" }} disabled>
                            {btn.btn_name}
                          </button>
                          <span className="badge bg-info" style={{ fontSize: 10 }}>{btn.page_count || 0} pages</span>
                        </div>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-success" onClick={() => openPageEditor(btn)} title="Manage Pages">
                            <i className="ri-pages-line" /> Pages
                          </button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEditBtn(btn)}>
                            <i className="ri-edit-line" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteBtn(btn.btn_id)}>
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ═══ RIGHT: Page Editor ═══ */}
          <div className="col-md-7">
            {!activeBtn ? (
              <div className="card"><div className="card-body text-center text-muted py-5">
                <i className="ri-arrow-left-line" style={{ fontSize: 40 }} /><br />
                Select a button from the left and click "Pages" to manage its pages.
              </div></div>
            ) : (
              <>
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ background: activeBtn.btn_color || "#405189", color: "#fff" }}>
                    <h6 className="mb-0">Pages for: {activeBtn.btn_name}</h6>
                    <button className="btn btn-light btn-sm" onClick={() => { setActiveBtn(null); setPages([]); resetPageForm(); }}>Close</button>
                  </div>
                  <div className="card-body">
                    {/* Existing pages list */}
                    {pagesLoading ? <div className="text-center"><span className="spinner-border spinner-border-sm" /></div> : (
                      <div className="mb-3">
                        {pages.length === 0 ? (
                          <div className="alert alert-info mb-2" style={{ fontSize: 12 }}>No pages yet. Add the first page below.</div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {pages.map((pg, idx) => (
                              <div key={pg.page_id} className="border rounded p-2 text-center" style={{ width: 110, cursor: "pointer", background: editPage?.page_id === pg.page_id ? "#e8f0fe" : "#fff" }}
                                onClick={() => handleEditPage(pg)}>
                                {pg.page_image ? (
                                  <img src={AppConst.BASE_URL + "/" + pg.page_image} alt="" style={{ height: 60, borderRadius: 3, border: "1px solid #dee2e6" }} />
                                ) : <div style={{ height: 60, background: "#f0f0f0", borderRadius: 3 }} />}
                                <div style={{ fontSize: 10, marginTop: 4 }}>Page {pg.page_order}</div>
                                <button className="btn btn-outline-danger btn-sm mt-1" style={{ fontSize: 9, padding: "1px 6px" }}
                                  onClick={(e) => { e.stopPropagation(); handleDeletePage(pg.page_id); }}>Delete</button>
                              </div>
                            ))}
                            {/* Add new page card */}
                            <div className="border border-dashed rounded p-2 text-center d-flex align-items-center justify-content-center"
                              style={{ width: 110, height: 100, cursor: "pointer", background: "#f8f9fa" }}
                              onClick={resetPageForm}>
                              <div><i className="ri-add-line" style={{ fontSize: 24, color: "#405189" }} /><div style={{ fontSize: 10 }}>Add Page</div></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Page form */}
                    <div className="border rounded p-3" style={{ background: "#fafbfe" }}>
                      <h6 style={{ color: "#405189" }}>{editPage ? `Edit Page ${editPage.page_order}` : "Add New Page"}</h6>
                      <div className="row g-2 mb-3">
                        <div className="col-4">
                          <label style={{ fontSize: 11 }}>Page Order</label>
                          <input type="number" className="form-control form-control-sm" value={pageOrder} onChange={e => setPageOrder(parseInt(e.target.value) || 1)} min={1} />
                        </div>
                        <div className="col-8">
                          <label style={{ fontSize: 11 }}>Page Image *</label>
                          <input type="file" className="form-control form-control-sm" accept="image/*" onChange={handlePageImageChange} />
                        </div>
                      </div>

                      {/* Image canvas + variables */}
                      {currentImgSrc && (
                        <div className="row">
                          {/* Variables palette */}
                          <div className="col-3">
                            <div style={{ maxHeight: 400, overflowY: "auto", fontSize: 11 }}>
                              <strong style={{ fontSize: 11 }}>Variables</strong>
                              {OT_VARIABLES.map(v => {
                                const added = pageVariables.some(pv => pv.key === v.key);
                                return (
                                  <div key={v.key} className="d-flex justify-content-between align-items-center mb-1 p-1 rounded"
                                    style={{ background: added ? "#d1e7dd" : "#fff", border: "1px solid #dee2e6", cursor: added ? "default" : "pointer", fontSize: 10 }}
                                    onClick={() => !added && addVariable(v)}>
                                    <span>{added && "✓ "}{v.label}</span>
                                    {!added && <i className="ri-add-circle-line text-primary" style={{ fontSize: 13 }} />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Canvas */}
                          <div className="col-6">
                            <div ref={canvasRef} style={{ position: "relative", width: "100%", border: "2px solid #405189", borderRadius: 4, overflow: "hidden", userSelect: "none" }}>
                              <img src={currentImgSrc} alt="Template" style={{ width: "100%", display: "block", pointerEvents: "none" }} draggable={false} />
                              {pageVariables.map((v, idx) => (
                                <div key={v.key} style={{
                                  position: "absolute", left: `${v.x}%`, top: `${v.y}%`,
                                  background: draggingIdx === idx ? "rgba(64,81,137,0.9)" : "rgba(64,81,137,0.75)",
                                  color: "#fff", padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600,
                                  cursor: "grab", whiteSpace: "nowrap", zIndex: draggingIdx === idx ? 100 : 10,
                                  transform: "translate(0, -50%)",
                                }} onMouseDown={e => handleMouseDown(idx, e)}>
                                  {`{${v.label}}`}
                                  <span style={{ marginLeft: 4, cursor: "pointer", color: "#ff9999" }}
                                    onClick={e => { e.stopPropagation(); removeVariable(idx); }}>×</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Variable properties */}
                          <div className="col-3">
                            <div style={{ maxHeight: 400, overflowY: "auto", fontSize: 10 }}>
                              <strong style={{ fontSize: 11 }}>Placed ({pageVariables.length})</strong>
                              {pageVariables.map((v, idx) => (
                                <div key={v.key} className="mb-1 p-1 rounded" style={{ border: "1px solid #dee2e6" }}>
                                  <div className="d-flex justify-content-between">
                                    <strong style={{ color: "#405189" }}>{v.label}</strong>
                                    <span style={{ cursor: "pointer", color: "red" }} onClick={() => removeVariable(idx)}>×</span>
                                  </div>
                                  <div className="row g-1">
                                    <div className="col-6"><input type="number" className="form-control" style={{ fontSize: 10, padding: 2 }} value={v.x} step={0.5} onChange={e => updateVariable(idx, "x", +e.target.value || 0)} /></div>
                                    <div className="col-6"><input type="number" className="form-control" style={{ fontSize: 10, padding: 2 }} value={v.y} step={0.5} onChange={e => updateVariable(idx, "y", +e.target.value || 0)} /></div>
                                    <div className="col-4"><input type="number" className="form-control" style={{ fontSize: 10, padding: 2 }} value={v.fontSize} min={6} max={36} onChange={e => updateVariable(idx, "fontSize", +e.target.value || 12)} /></div>
                                    <div className="col-4"><input type="color" className="form-control p-0" style={{ height: 22 }} value={v.fontColor} onChange={e => updateVariable(idx, "fontColor", e.target.value)} /></div>
                                    <div className="col-4">
                                      <select className="form-control" style={{ fontSize: 10, padding: 2 }} value={v.fontWeight} onChange={e => updateVariable(idx, "fontWeight", e.target.value)}>
                                        <option value="normal">N</option><option value="bold">B</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="text-end mt-2">
                        {editPage && <button className="btn btn-outline-secondary btn-sm me-2" onClick={resetPageForm}>Cancel</button>}
                        <button className="btn btn-primary btn-sm" onClick={handleSavePage} disabled={savingPage}>
                          {savingPage ? "Saving..." : (editPage ? "Update Page" : "Add Page")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OtFormat;